import { Button, Grid } from '@material-ui/core';
import React, { useState } from 'react';
import {
  CourierPreferencesJson,
  maybeUpdateCourierPreferences,
} from '../../fetch/fetchCourierPreferences';
import { DeliveriesConfigAddressAutocomplete } from './DeliveriesConfigAddressAutocomplete/DeliveriesConfigAddressAutocomplete';
import { DeliveriesConfigInput } from './DeliveriesConfigInput/DeliveriesConfigInput';

interface DeliveriesConfigFormProps {
  courierId: number;
  courierPreference: CourierPreferencesJson;
  courierPreferenceEndpointDraft: {
    endpoint?: {
      lon: number;
      lat: number;
    };
    endpointAddress?: string;
  };
  updatePinpointDraggable: (status: boolean) => void;
  onChangeCourierPreferenceEndpointDraft: (
    lon?: number,
    lat?: number,
    name?: string
  ) => void;
  refreshData: () => void;
}

export const DeliveriesConfigForm = ({
  courierId,
  updatePinpointDraggable,
  onChangeCourierPreferenceEndpointDraft,
  refreshData,
  ...props
}: DeliveriesConfigFormProps) => {
  const [editable, setEditable] = useState(false);
  const [invalidInputs, setInvalidInputs] = useState<string[]>([]);
  const [preferenceDraft, setPreferenceDraft] =
    useState<CourierPreferencesJson>();

  const closeEdit = () => {
    setEditable(false);

    /* Reset all form to default value */
    setPreferenceDraft(props.courierPreference);
    setInvalidInputs([]);
    updatePinpointDraggable(false);
  };

  const enableEdit = () => {
    setEditable(true);
    updatePinpointDraggable(true);
  };

  const addInvalidInput = (inputName: string) => {
    /* Add conditional to avoid duplication */
    if (!invalidInputs.includes(inputName)) {
      /* Add the invalid input */
      setInvalidInputs([...invalidInputs, inputName]);
    }
  };

  const removeInvalidInput = (inputName: string) => {
    /* Add conditional to only remove the registered invalidInputs */
    const newInvalidInputs = [...invalidInputs].filter(
      invalidInput => invalidInput !== inputName
    );
    if (newInvalidInputs.length < invalidInputs.length) {
      setInvalidInputs(newInvalidInputs);
    }
  };

  const onChangeTargetNumberOfDeliveries = (newValue: number) => {
    if (newValue !== preferenceDraft.targetNumberOfDeliveries) {
      const newPreferenceDraft: CourierPreferencesJson = {
        ...preferenceDraft,
        targetNumberOfDeliveries: newValue,
      };
      setPreferenceDraft(newPreferenceDraft);
    }
  };

  const onChangeMaxNumberOfDeliveries = (newValue: number) => {
    if (newValue !== preferenceDraft.maxNumberOfDeliveries) {
      const newPreferenceDraft: CourierPreferencesJson = {
        ...preferenceDraft,
        maxNumberOfDeliveries: newValue,
      };
      setPreferenceDraft(newPreferenceDraft);
    }
  };

  const onSaveCourierPreferenceEndpointDraft = async () => {
    const endpointDraft = { ...props.courierPreferenceEndpointDraft };
    const draft = { ...(preferenceDraft || props.courierPreference) };

    if (endpointDraft.endpoint?.lon && endpointDraft.endpoint?.lat) {
      draft.endpoint = endpointDraft.endpoint;
      draft.endpointAddress = endpointDraft.endpointAddress;
    }

    const result = await maybeUpdateCourierPreferences(draft);
    if (result) {
      /* Update the selected courier's preference */
      refreshData();
    }

    return result;
  };

  const onClickAction = async () => {
    if (editable) {
      await onSaveCourierPreferenceEndpointDraft();
      closeEdit();
    } else {
      enableEdit();
    }
  };

  return (
    <>
      <Grid container justifyContent="space-between">
        <Grid item>
          <h3>Courier preferences</h3>
        </Grid>
        <Grid item>
          {editable && (
            <Button color="primary" onClick={closeEdit}>
              cancel
            </Button>
          )}
          <Button
            color="primary"
            variant="outlined"
            onClick={onClickAction}
            disabled={editable && invalidInputs.length > 0}
          >
            {editable ? 'Save' : 'Edit Preferences'}
          </Button>
        </Grid>
      </Grid>
      <DeliveriesConfigInput
        name="targetNumberOfDeliveries"
        label="Target number of deliveries"
        defaultValue={props.courierPreference?.targetNumberOfDeliveries}
        editable={editable}
        onChange={onChangeTargetNumberOfDeliveries}
        addInvalidInput={addInvalidInput}
        removeInvalidInput={removeInvalidInput}
      />
      <DeliveriesConfigInput
        name="maxNumberOfDeliveries"
        label="Max number of deliveries"
        defaultValue={props.courierPreference?.maxNumberOfDeliveries}
        editable={editable}
        onChange={onChangeMaxNumberOfDeliveries}
        addInvalidInput={addInvalidInput}
        removeInvalidInput={removeInvalidInput}
      />
      <DeliveriesConfigAddressAutocomplete
        text={props.courierPreference?.endpointAddress}
        lat={props.courierPreference?.endpoint?.lat || null}
        lon={props.courierPreference?.endpoint?.lon || null}
        draft={props.courierPreferenceEndpointDraft}
        editable={editable}
        updatePinpointDraggable={updatePinpointDraggable}
        onChangeDraft={onChangeCourierPreferenceEndpointDraft}
      />
    </>
  );
};
